import { existsSync } from "node:fs";
import { join } from "node:path";

function nodeCwd() {
  return typeof process !== "undefined" && typeof process.cwd === "function" ? process.cwd() : "";
}

/**
 * All structured JSON (adventures, scenes, …) for the app. Prefer `data/json` (produced
 * by `scripts/split-content.mjs`); fall back to `content/json` until the split is committed.
 * Server-only — returns `""` in the browser (Marko client chunks must not read the filesystem).
 */
export function getDataJsonRoot() {
  const c = nodeCwd();
  if (!c) return "";
  const fromData = join(c, "data", "json");
  if (existsSync(fromData)) return fromData;
  if (existsSync(join(c, "content", "json"))) return join(c, "content", "json");
  return fromData;
}

/**
 * Sibling `content/` checkout (`src/assets/json`) — used when UI meta sidecars are not yet
 * copied into `data/json/{uiLang}/meta/` (local dev with Content API).
 */
export function getContentRepoJsonRoot() {
  const c = nodeCwd();
  if (!c) return "";
  const fromEnv = String(process.env.CONTENT_REPO_ROOT ?? "").trim();
  if (fromEnv && existsSync(fromEnv)) return join(fromEnv, "src", "assets", "json");
  const sibling = join(c, "..", "content", "src", "assets", "json");
  if (existsSync(sibling)) return sibling;
  return "";
}

/**
 * Sibling `content/` checkout (`src/assets`) — audio registry + MP3s during local dev when
 * `data/content` / `cdn/` mirrors lag behind the content repo.
 */
export function getContentRepoAssetRoot() {
  const c = nodeCwd();
  if (!c) return "";
  const fromEnv = String(process.env.CONTENT_REPO_ROOT ?? "").trim();
  if (fromEnv && existsSync(fromEnv)) return join(fromEnv, "src", "assets");
  const sibling = join(c, "..", "content", "src", "assets");
  if (existsSync(sibling)) return sibling;
  return "";
}

/**
 * Small sidecar JSON and anything left under `data/content/` after `content:split`.
 * Only the split script reads the full `content/` tree; runtime must not load assets from `content/`.
 */
export function getAssetContentRoot() {
  const c = nodeCwd();
  if (!c) return "";
  return join(c, "data", "content");
}

/**
 * PDFs on disk: `cdn/pdf` first (split output that mirrors the pull zone), then `data/content/pdf`.
 */
export function getPdfSourceRoot() {
  const c = nodeCwd();
  if (!c) return "";
  const cdnPdf = join(c, "cdn", "pdf");
  if (existsSync(cdnPdf)) return cdnPdf;
  return join(c, "data", "content", "pdf");
}

/**
 * Resolve a site-relative asset (e.g. `img/scenes/foo.webp`, no leading slash) on disk.
 * Order: `cdn/` (local mirror of what you push), then `data/content/`, then `public/`.
 * @param {string} relPath
 * @returns {string | null} absolute path if found
 */
export function resolveAssetFileOnDisk(relPath) {
  const r = String(relPath || "").replace(/^\/+/, "");
  if (!r) return null;
  const c = nodeCwd();
  if (!c) return null;
  for (const base of [join(c, "cdn"), join(c, "data", "content"), join(c, "public")]) {
    const full = join(base, r);
    if (existsSync(full)) return full;
  }
  const contentRepoRoot = getContentRepoAssetRoot();
  if (contentRepoRoot && r.startsWith("audio/")) {
    const fromRepo = join(contentRepoRoot, r);
    if (existsSync(fromRepo)) return fromRepo;
  }
  return null;
}

/**
 * Staged tree for `scripts/split-content.mjs` (not committed) — used for on-disk
 * `existsSync` checks in dev; paths mirror the CDN, e.g. `cdn/audio/...` → URL `/audio/...`.
 */
export function getCdnStageRoot() {
  const c = nodeCwd();
  if (!c) return "";
  return join(c, "cdn");
}
